import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumMetricType } from "./enums/enumMetricType.generated";

// Model Objects

export class QcTestMetricLineReadOnly extends ModelObject<QcTestMetricLineReadOnly> {

    /**
    * Qc Test to which this metric belongs.
    */
    public qcTestId: Guid = Guid.createEmpty();
    /**
    * Default ordering sequence value.
    */
    public sequence: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.qcTestId = Guid.parseOrEmpty(jsonSerializedObject.qcTestId);
          this.sequence = jsonSerializedObject.sequence;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.qcTestId = this.qcTestId.toString();
        toRet.sequence = this.sequence;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "qcTestId", rule: (v: any) => !v.isEmpty() || "QcTestMetricLineReadOnly.QcTestId cannot be empty"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
    ];
}


export class QcTestMetricLine extends ModelObject<QcTestMetricLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    * Preserves leading and trailing whitespace.
    */
    public notes: string = "";
    /**
    * Formula for calculating this metric from the measurements of other metrics. If formula is populated then this metric is expected to be calculated not measured.  Format of this formula is yet to be defined.
    * Not Nullable
    * Max Length : 100000
    * Preserves leading and trailing whitespace.
    */
    public formula: string = "";
    /**
    * Is this metric shown on the results graph?
    */
    public isGraphable: boolean = false;
    private _maximumDecimalPlaces: number | null = null;
    /**
    * The maximum number of decimal places allowed to be given for a measurement.
    */
    public get maximumDecimalPlaces(): number | null { return this._maximumDecimalPlaces }
    public set maximumDecimalPlaces(value: number | null) {
      this._maximumDecimalPlaces = value === undefined ? null : value;
    }
    private _maximumPermittedValue: number | null = null;
    /**
    * If provided, gives the maximum permitted value of the measurement, allowing basic validation of user entries.
    */
    public get maximumPermittedValue(): number | null { return this._maximumPermittedValue }
    public set maximumPermittedValue(value: number | null) {
      this._maximumPermittedValue = value === undefined ? null : value;
    }
    private _minimumPermittedValue: number | null = null;
    /**
    * If provided, gives the minimum permitted value of the measurement, allowing basic validation of user entries.
    */
    public get minimumPermittedValue(): number | null { return this._minimumPermittedValue }
    public set minimumPermittedValue(value: number | null) {
      this._minimumPermittedValue = value === undefined ? null : value;
    }
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public name: string = "";
    /**
    * The suffix to display as measurement units.
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public unitSuffix: string = "";
    /**
    * The type of metric being captured. Giving a metric a type allows the system to assign probe values straight into assemblies.
    */
    public type: EnumMetricType = EnumMetricType.custom;
    /**
    * Is this metric required to be measured when other metrics of the same test are measured?
    */
    public isMandatory: boolean = false;
    public readOnly: QcTestMetricLineReadOnly = new QcTestMetricLineReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.notes = jsonSerializedObject.notes;
          this.formula = jsonSerializedObject.formula;
          this.isGraphable = jsonSerializedObject.isGraphable;
          this.maximumDecimalPlaces = jsonSerializedObject.maximumDecimalPlaces == null ? null : jsonSerializedObject.maximumDecimalPlaces;
          this.maximumPermittedValue = jsonSerializedObject.maximumPermittedValue == null ? null : jsonSerializedObject.maximumPermittedValue;
          this.minimumPermittedValue = jsonSerializedObject.minimumPermittedValue == null ? null : jsonSerializedObject.minimumPermittedValue;
          this.name = jsonSerializedObject.name;
          this.unitSuffix = jsonSerializedObject.unitSuffix;
          this.type = EnumMetricType.getByValue(jsonSerializedObject.type, this.type);
          this.isMandatory = jsonSerializedObject.isMandatory;
          this.readOnly = new QcTestMetricLineReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.notes = this.notes;
        toRet.formula = this.formula;
        toRet.isGraphable = this.isGraphable;
        toRet.maximumDecimalPlaces = this.maximumDecimalPlaces == null ? null : this.maximumDecimalPlaces;
        toRet.maximumPermittedValue = this.maximumPermittedValue == null ? null : this.maximumPermittedValue;
        toRet.minimumPermittedValue = this.minimumPermittedValue == null ? null : this.minimumPermittedValue;
        toRet.name = this.name;
        toRet.unitSuffix = this.unitSuffix;
        toRet.type = this.type.value;
        toRet.isMandatory = this.isMandatory;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "QcTestMetricLine.Id cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "formula", rule: (v: any) => v == null || v.length <= 100000 || "Formula has too many characters (100000)"},
            {field: "maximumDecimalPlaces", rule: (v: any) => v == null || v >= -2147483648 || "MaximumDecimalPlaces is not greater than minimum allowed value: -2147483648"},
            {field: "maximumDecimalPlaces", rule: (v: any) => v == null || v <= 2147483647 || "MaximumDecimalPlaces is above maximum allowed value: 2147483647"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "unitSuffix", rule: (v: any) => v == null || v.length <= 1000 || "UnitSuffix has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class QcTestHeader extends ModelObject<QcTestHeader> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Is this test performable on material production assemblies?
    */
    public isForMaterialProduction: boolean = false;
    /**
    * Is this test performable on product packaging assemblies?
    */
    public isForProductPackaging: boolean = false;
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    * Preserves leading and trailing whitespace.
    */
    public name: string = "";
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    * Preserves leading and trailing whitespace.
    */
    public notes: string = "";
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.isForMaterialProduction = jsonSerializedObject.isForMaterialProduction;
          this.isForProductPackaging = jsonSerializedObject.isForProductPackaging;
          this.name = jsonSerializedObject.name;
          this.notes = jsonSerializedObject.notes;
          this.isHidden = jsonSerializedObject.isHidden;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.isForMaterialProduction = this.isForMaterialProduction;
        toRet.isForProductPackaging = this.isForProductPackaging;
        toRet.name = this.name;
        toRet.notes = this.notes;
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "QcTestHeader.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
    ];
}


export class QcTest extends ModelObject<QcTest> {

    public header: QcTestHeader = new QcTestHeader();
    public metricLines: (QcTestMetricLine)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new QcTestHeader(jsonSerializedObject.header);
          this.metricLines = jsonSerializedObject.metricLines.map((v: any) => new QcTestMetricLine(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.metricLines = this.metricLines.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "metricLines", rule: (v: any) => !!v || "metricLines is required"},
            {field: "metricLines", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as QcTestMetricLine[]).reduce((acc: Array<string>, curr: QcTestMetricLine) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class QcTestService extends ServiceBase {

  public async getAllQcTests(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(QcTest)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.qcTests.map((v: any) => new QcTest(v));;
    return this.makeJsonRequest("qcTest/v1/getAllQcTests", req, false, extractor, opts);
  };

  public async getQcTest(qcTestId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<QcTest> {
    const req = {
    tenantId:api.tenantId.toString(),
    qcTestId:qcTestId.toString(),
    };
    const extractor = (response:any) => new QcTest(response.qcTest);;
    return this.makeJsonRequest("qcTest/v1/getQcTest", req, false, extractor, opts);
  };

  public async getQcTests(qcTestIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(QcTest)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    qcTestIds:qcTestIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.qcTests.map((v: any) => new QcTest(v));;
    return this.makeJsonRequest("qcTest/v1/getQcTests", req, false, extractor, opts);
  };

  public async saveQcTest(qcTest: QcTest, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    qcTest:qcTest.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("qcTest/v1/saveQcTest", req, true, extractor, opts);
  };

  public async setQcTestIsHidden(qcTestId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    qcTestId:qcTestId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("qcTest/v1/setQcTestIsHidden", req, true, extractor, opts);
  };
}

export const qcTestService = new QcTestService();